import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SingleForecastsChartDataResponse } from '@api-response-types/single-forecasts-chart-data.response';
import { GetAbcXyzAnalysisDto } from '@api-types/dtos/get-abc-xyz-analysis.dto';
import { GetPlanningAccuracyWidgetDataDto } from '@api-types/dtos/get-planning-accuracy-widget-data.dto';
import { GetYearlyComparisonWidgetDataDto } from '@api-types/dtos/get-yearly-comparison-widget-data.dto';
import {
  WidgetPortfolioTotals,
  WidgetPortfolioTotalsByYear,
} from '@api-types/dtos/widget-portfolio-totals.dto';
import { Measure } from '@api-types/enums/measure.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { HighchartsChartData } from '@api-types/responses/highcharts-chart-data.response';
import { FilterValue } from '@api-types/types/filter-value.type';
import { GetSingleForecastsBody } from '@api-types/types/single-forecasts-body.type';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InsightsHttpClient {
  private readonly http = inject(HttpClient);

  public baseUrl = environment.api.url;

  getPortfolioTotals(
    forecastId: string,
    factorColumn?: string,
    year?: string,
    years?: string[],
  ): Observable<WidgetPortfolioTotalsByYear> {
    const params: {
      factorColumn?: string;
      year?: string;
      years?: string;
      resolution?: string;
    } = {};

    if (factorColumn) {
      params.factorColumn = factorColumn;
    }
    if (year && !years) {
      params.year = year;
    } else if (years && !year) {
      params.years = years.join(',');
    }

    return this.http.get<WidgetPortfolioTotalsByYear>(
      `${this.baseUrl}/insights/portfolio-totals/forecast/${forecastId}`,
      { params },
    );
  }

  getPortfolioTotalsMultiYear(
    forecastId: string,
    factorColumn?: string,
    year?: string,
    years?: string[],
    resolution?: Resolution,
  ): Observable<WidgetPortfolioTotals> {
    const params: {
      factorColumn?: string;
      year?: string;
      years?: string;
      resolution?: string;
    } = {};

    if (factorColumn) {
      params.factorColumn = factorColumn;
    }
    if (year && !years) {
      params.year = year;
    } else if (years && !year) {
      params.years = years.join(',');
    }
    if (resolution) {
      params.resolution = resolution;
    }
    return this.http.get<WidgetPortfolioTotals>(
      `${this.baseUrl}/insights/portfolio-totals-multi-year/forecast/${forecastId}`,
      { params },
    );
  }

  getYearToDateTrend(
    resultSetId: string,
    filterValues: FilterValue[],
    selectedFilterName: string,
  ): Observable<HighchartsChartData> {
    return this.http.post<HighchartsChartData>(
      `${this.baseUrl}/insights/results/${resultSetId}/year-to-date-trend/${selectedFilterName}`,
      filterValues,
    );
  }

  getSingleForecastsData(
    resultSetId: string,
    selectedFilterName: string,
    filterValues: FilterValue[],
    resolution: Resolution,
    factorColumn?: string,
  ): Observable<SingleForecastsChartDataResponse> {
    const body: GetSingleForecastsBody = {
      selectedFilterName,
      filters: filterValues,
      resolution,
    };
    if (factorColumn) {
      body.factorColumn = factorColumn;
    }
    return this.http.post<SingleForecastsChartDataResponse>(
      `${this.baseUrl}/insights/results/${resultSetId}/single-forecasts`,
      body,
    );
  }

  getYearlyComparisonData(
    resultSetId: string,
    filterValue: FilterValue | undefined,
    selectedFilterName: string | undefined,
    resolution?: Resolution,
    factorColumn?: string,
  ) {
    return this.http.post<GetYearlyComparisonWidgetDataDto>(
      `${this.baseUrl}/insights/results/${resultSetId}/yearly-comparison`,
      {
        filterValue,
        selectedFilterName,
        resolution,
        factorColumn,
      },
    );
  }

  getPlanningAccuracyData(
    configurationId: string,
    forecastVersionId: string,
    planningPeriod: number,
    filterGroup: string,
    filterOption: FilterValue,
    resolution?: Resolution,
    additionalTimeSeriesName?: string,
  ): Observable<GetPlanningAccuracyWidgetDataDto> {
    const params: {
      filterGroup: string;
      filterOption: string | number | boolean;
      planningPeriod: string;
      resolution?: string;
      additionalTimeSeriesName?: string;
    } = {
      filterGroup,
      filterOption: filterOption ?? 'null',
      planningPeriod: planningPeriod.toString(),
    };

    if (resolution) {
      params.resolution = resolution;
    }

    if (additionalTimeSeriesName) {
      params.additionalTimeSeriesName = additionalTimeSeriesName;
    }

    return this.http.get<GetPlanningAccuracyWidgetDataDto>(
      `${this.baseUrl}/insights/configuration/${configurationId}/forecastVersion/${forecastVersionId}/planning-accuracy`,
      { params },
    );
  }

  getAbcXyzAnalysisData(
    configurationId: string,
    forecastVersionId: string,
    abcBinLimits: number[],
    xyzBinLimits: number[],
    planningPeriod: number,
    errorMeasure: Measure,
    timePeriodBackwardsInMonths: number,
    groups?: string[],
    targetUnit?: string,
  ): Observable<GetAbcXyzAnalysisDto> {
    const params: {
      forecastVersionId: string;
      abcBinLimits: number[];
      xyzBinLimits: number[];
      planningPeriod: number;
      errorMeasure: Measure;
      timePeriodBackwardsInMonths?: number;
      groups?: string[];
      targetUnit?: string;
    } = {
      forecastVersionId,
      abcBinLimits,
      xyzBinLimits,
      planningPeriod,
      errorMeasure,
    };
    if (groups) {
      params.groups = groups;
    }
    if (timePeriodBackwardsInMonths) {
      params.timePeriodBackwardsInMonths = timePeriodBackwardsInMonths;
    }
    if (targetUnit) {
      params.targetUnit = targetUnit;
    }
    return this.http.get<GetAbcXyzAnalysisDto>(
      `${this.baseUrl}/insights/configuration/${configurationId}/forecastVersion/${forecastVersionId}/abc-xyz-analysis`,
      { params },
    );
  }
}
